import { useState } from 'react'
import { ArrowLeft, Trash2, AlertTriangle, Loader2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'

export default function ManageAccount({ onBack }) {
  const { signOut } = useAuth()
  const [showDeleteWarning, setShowDeleteWarning] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteError, setDeleteError] = useState(null)

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
      }}>
        <button
          onClick={onBack}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '9999px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--bg-tertiary)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: '1.25rem', fontWeight: '700' }}>Manage Account</h1>
      </div>

      {/* Delete Account section */}
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.75rem',
        border: '1px solid var(--border-color)',
        padding: '1.25rem',
      }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: '600', marginBottom: '0.5rem' }}>Delete Account</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: '1rem' }}>
          Permanently delete your account and remove your personal information. Your scores and match history will remain but show "Deleted Player."
        </p>
        <button
          onClick={() => setShowDeleteWarning(true)}
          style={{
            width: '100%',
            height: '44px',
            borderRadius: '9999px',
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#ef4444',
            backgroundColor: 'transparent',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
          }}
        >
          <Trash2 size={16} />
          Delete Account
        </button>
      </div>

      {/* Delete Warning Modal (Step 1) */}
      {showDeleteWarning && (
        <div className="modal-overlay" onClick={() => setShowDeleteWarning(false)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="confirm-content" style={{ textAlign: 'center' }}>
              <AlertTriangle size={48} style={{ color: '#ef4444', marginBottom: '0.75rem' }} />
              <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.75rem' }}>Delete Your Account?</h3>
              <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6, textAlign: 'left' }}>
                <p style={{ marginBottom: '0.5rem' }}>This action is <strong style={{ color: '#ef4444' }}>permanent and cannot be undone</strong>.</p>
                <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.5rem' }}>
                  <li>Your personal information (name, email, handicap) will be removed.</li>
                  <li>Your matches and scores will remain, but will show "Deleted Player."</li>
                  <li>Your follows and followers will be removed.</li>
                  <li>Your login will be permanently deleted.</li>
                </ul>
              </div>
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button
                onClick={() => setShowDeleteWarning(false)}
                style={{
                  flex: 1,
                  height: '44px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowDeleteWarning(false); setShowDeleteConfirm(true); setDeleteInput(''); setDeleteError(null) }}
                style={{
                  flex: 1,
                  height: '44px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal (Step 2) */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => { if (!deleteLoading) setShowDeleteConfirm(false) }}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <div className="confirm-content" style={{ textAlign: 'center' }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '0.75rem' }}>Confirm Deletion</h3>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Type <strong style={{ color: 'var(--text-primary)' }}>DELETE</strong> to confirm.
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="Type DELETE"
                disabled={deleteLoading}
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '0 0.75rem',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  letterSpacing: '0.1em',
                  textAlign: 'center',
                  backgroundColor: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              {deleteError && (
                <div style={{ color: '#ef4444', fontSize: '0.8125rem', marginTop: '0.75rem' }}>
                  {deleteError}
                </div>
              )}
            </div>
            <div className="modal-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={async () => {
                  setDeleteLoading(true)
                  setDeleteError(null)
                  try {
                    const { data: { session } } = await supabase.auth.getSession()
                    if (!session) {
                      setDeleteError('Session expired. Please sign in again.')
                      setDeleteLoading(false)
                      return
                    }
                    const resp = await fetch('/api/delete-account', {
                      method: 'POST',
                      headers: { Authorization: 'Bearer ' + session.access_token },
                    })
                    const result = await resp.json()
                    if (!resp.ok) {
                      setDeleteError(result.error || 'Failed to delete account')
                      setDeleteLoading(false)
                      return
                    }
                    await signOut()
                  } catch (err) {
                    setDeleteError('Something went wrong. Please try again.')
                    setDeleteLoading(false)
                  }
                }}
                disabled={deleteInput !== 'DELETE' || deleteLoading}
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '700',
                  color: '#fff',
                  backgroundColor: '#ef4444',
                  border: 'none',
                  cursor: (deleteInput !== 'DELETE' || deleteLoading) ? 'not-allowed' : 'pointer',
                  opacity: (deleteInput !== 'DELETE' || deleteLoading) ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}
              >
                {deleteLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={16} />}
                {deleteLoading ? 'Deleting...' : 'Delete Account'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteLoading}
                style={{
                  width: '100%',
                  height: '44px',
                  borderRadius: '9999px',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  cursor: deleteLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
